import React from "react";
import { Assets, Colors, FontFamily, Modal, PrimaryButton } from "utils/imports.utils";
import { ButtonSize, ButtonTypes } from "utils/interface.utils";
import { getProjectArLink, toastifyError, toastifySuccess } from "utils/functions.utils";
import { STRINGS } from "constants/strings.constant";
import { PublishedArProjectCardProps } from "interfaces/payment_modal.interface";
import { useNavigate } from "react-router-dom";
import ModalCloseButton from "components/modal_close_button/modal_close_button.component";
import ShareLinkField from "components/share_link_field/share_link_field.component";
import "./published_ar_project_card.component.scss";

const MODAL_STYLES = { width: 'min(500px, calc(100vw - 32px))', borderRadius: '28px' };

// Shown once a project is published: how to share it, and where to go next.
const PublishedArProjectCard = (props: PublishedArProjectCardProps) => {
    const navigate = useNavigate();
    const arLink = props.projectId ? getProjectArLink(props.projectId) : '';

    const copyArLink = async () => {
        try {
            await navigator.clipboard.writeText(arLink);
            toastifySuccess(STRINGS.preview.linkCopied);
        } catch (error) {
            console.error('Failed to copy link:', error);
            toastifyError(STRINGS.preview.linkCopyFailed);
        }
    };

    return (
        <Modal isOpen={props.isOpen} customStyles={MODAL_STYLES}>
            <div className="published_ar_project_card">
                <ModalCloseButton onClose={props.onClose} />
                <img className="published_ar_project_card_badge" src={Assets.publish_success} alt={STRINGS.publishedModal.successAlt} />
                <h2 className="published_ar_project_card_title">{STRINGS.publishedModal.title}</h2>
                <p className="published_ar_project_card_description">{STRINGS.publishedModal.description}</p>
                {arLink && (
                    <div className="published_ar_project_card_link">
                        <div className="published_ar_project_card_link_label">{STRINGS.publishedModal.shareLink}</div>
                        <ShareLinkField link={arLink} onCopy={copyArLink} />
                    </div>
                )}
                <div className="published_ar_project_card_actions">
                    <div className="published_ar_project_card_actions_button">
                        <PrimaryButton
                            text={STRINGS.publishedModal.backToProjects}
                            type={ButtonTypes.OUTLINED}
                            size={ButtonSize.LARGE}
                            fontFamily={FontFamily.semiBold}
                            color={Colors.primaryTextColor}
                            style={{ border: `1.5px solid ${Colors.primaryTextColor}` }}
                            onClick={(e: React.MouseEvent) => {
                                // The card renders in a portal, but the click still bubbles up the component tree to the
                                // project row that opened it, which would open that project's editor.
                                e.stopPropagation();
                                props.onClose();
                                navigate('/welcome');
                            }}
                        />
                    </div>
                    {props.onDownloadQr && (
                        <div className="published_ar_project_card_actions_button">
                            <PrimaryButton
                                text={STRINGS.publishedModal.downloadQr}
                                left_icon={Assets.download}
                                type={ButtonTypes.PRIMARY}
                                size={ButtonSize.LARGE}
                                fontFamily={FontFamily.semiBold}
                                iconSize={{ width: 18, height: 18 }}
                                onClick={props.onDownloadQr}
                            />
                        </div>
                    )}
                    {!props.onDownloadQr && props.projectId && (
                        <div className="published_ar_project_card_actions_button">
                            <PrimaryButton
                                text={STRINGS.publishedModal.seePreview}
                                type={ButtonTypes.PRIMARY}
                                size={ButtonSize.LARGE}
                                fontFamily={FontFamily.semiBold}
                                onClick={(e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    navigate(`/preview/${props.projectId}`);
                                }}
                            />
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    )
}

export default PublishedArProjectCard;
